
import React, { useState, useEffect } from 'react';
import { DndContext, closestCorners, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { PipelineColumn, Prospect } from '@/types/pipeline';
import { usePipelineData } from '@/hooks/pipeline/use-pipeline-data';
import { usePipelineDrag } from '@/hooks/pipeline/use-pipeline-drag';
import PipelineBoardContent from './PipelineBoardContent';
import PipelineBoardDetails from './PipelineBoardDetails';
import UpdatingNotification from '@/components/ui/UpdatingNotification';
import { usePipelineStages } from '@/hooks/pipeline/use-pipeline-stages';

interface PipelineBoardProps {
  initialColumns: PipelineColumn[];
}

const PipelineBoard = ({ initialColumns }: PipelineBoardProps) => {
  const [columns, setColumns] = useState<PipelineColumn[]>(initialColumns);
  const [selectedProspect, setSelectedProspect] = useState<Prospect | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const { data: latestData } = usePipelineData();
  const { pipelineStages } = usePipelineStages();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Sync columns whenever latestData or pipelineStages changes
  useEffect(() => {
    if (latestData) {
      console.log('[PipelineBoard] Latest data received, updating columns');
      
      // Ensure the data is properly typed as Prospect[]
      const typedProspects = latestData as unknown as Prospect[];
      
      const updatedColumns = pipelineStages.map(stage => ({
        id: stage.id,
        title: stage.title,
        prospects: typedProspects.filter(p => p.stage === stage.id)
      }));
      
      console.log('[PipelineBoard] Updated columns structure:', updatedColumns);
      setColumns(updatedColumns);
    }
  }, [latestData, pipelineStages]);

  const {
    activeProspect,
    isUpdating,
    handleDragStart,
    handleDragOver,
    handleDragEnd
  } = usePipelineDrag(columns, setColumns);

  const handleProspectClick = (prospect: Prospect) => {
    console.log(`[Prospect Click] Selected prospect:`, prospect);
    setSelectedProspect(prospect);
    setIsDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setIsDetailsOpen(false);
    setSelectedProspect(null);
  };

  const handleUpdateProspect = (id: string, updatedProspect: Partial<Prospect>) => {
    // Handle updating prospect details
    console.log(`[Update Prospect] Updating prospect ${id}:`, updatedProspect);
    // This would typically call an update API or method
  };

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <PipelineBoardContent
          columns={columns}
          activeProspect={activeProspect}
          onProspectClick={handleProspectClick}
        />
      </DndContext>

      <PipelineBoardDetails
        prospect={selectedProspect}
        isOpen={isDetailsOpen}
        onClose={handleCloseDetails}
        onUpdate={handleUpdateProspect}
      />
      
      <UpdatingNotification 
        isVisible={isUpdating} 
        message="Updating" 
        status="updating"
        position="bottom-left"
      />
    </>
  );
};

export default PipelineBoard;
